import { useStore } from 'effector-react';
import { ButtonContainer } from 'features/verify/ui/button-container';
import { toSentenceCase } from 'libs/to-sentence-case';
import { css } from 'linaria';
import { styled } from 'linaria/react';
import { useLocalization } from 'model/localization.model';
import { RootDomain } from 'model/root.model';
import { countries$, sessionConfig$ } from 'model/session.model';
import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { Button } from 'ui/atoms/button';
import { Subtitle, Title } from 'ui/typography';

const BottomSliderOverlay = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: #000000;
  opacity: 0;
  transition: opacity .3s ease-in-out;
`;
const BottomSliderContent = styled.div`
  position: absolute;
  height: 100%;
  left: 16px;
  right: 16px;
  display: flex;
  flex-direction: column;
  bottom: -53px;
  background: var(--background);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  transition: transform .3s cubic-bezier(.6, .34, .16, 1.22);
  transform: translateY(100%);
`;
const BottomSliderContainer = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;
const containerVisible = css`
  pointer-events: auto;
`;
const contentVisible = css`
  transform: translateY(0);
`;
const overlayVisible = css`
  opacity: .5;
`;
const buttonsClass = css`
  margin: auto 32px calc(39px + 53px);
`;
const SelectContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  padding: 8px 8px 8px 16px;
  margin: 32px 32px 0 32px;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: var(--text-primary);
  border: 1px solid var(--text-secondary);
  border-radius: 4px;
  background: white;
`;
const selectFocusedClass = css`
  border-color: var(--primary-color);
`;
const Select = styled.select`
  max-width: 0;
  width: 0;
  max-height: 0;
  height: 0;
  opacity: 0;
  position: absolute;
  bottom: 0;
`;
const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 16px 32px;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: var(--text-primary);
`;
const DocumentsListItem = styled.div`
  display: flex;
  align-items: center;
  margin-top: 16px;

  svg {
    margin-right: 18px;
  }
`;
const Text = styled.div`
  margin-top: 32px;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: var(--text-primary);
`;

export const changeAcceptedDocumentsVisible = RootDomain.createEvent<boolean>();
const acceptedDocumentsVisible$ = RootDomain.createStore(false)
	.on(changeAcceptedDocumentsVisible, (_, visible) => visible);

export const AcceptedDocuments = () => {
	const t = useLocalization();
	const visible = useStore(acceptedDocumentsVisible$);
	const session = useStore(sessionConfig$);
	const selectRef = useRef(null);
	const [countryIcao, changeCountryIcao] = useState(null);
	const [selectFocused, changeSelectFocused] = useState(false);
	const countries = useStore(countries$);
	const country = countryIcao && countries
		.find(item => item.icao_code === countryIcao);
	const documents = country
		? country
			.documents
			.filter(item => item.document_type !== null)
			.map(item => ({
				type: item.document_type,
				title: toSentenceCase(t(`doc.type.${item.document_type}`)),
			}))
		: [];

	useEffect(() => {
		if (!visible) {
			changeCountryIcao(null);
		}
	}, [visible]);

	return (
		<BottomSliderContainer className={visible && containerVisible}>
			<BottomSliderOverlay className={visible && overlayVisible} onClick={() => changeAcceptedDocumentsVisible(false)}/>
			<BottomSliderContent className={visible && contentVisible}>
				<Title
					className={titleClass}
				>
					{t('accepted.docs.title')}
				</Title>
				<Subtitle
					className={subtitleClass}
				>
					{t('accepted.docs.subtitle', {
						company: session.company,
					})}
				</Subtitle>
				<SelectContainer className={selectFocused && selectFocusedClass} onClick={() => selectRef.current?.focus()}>
					{country ? t(`countries.${country.alpha2}`) : t('accepted.docs.select.placeholder')}
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M4 6L8 10L12 6" stroke="#262626" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					<Select
						ref={selectRef}
						value={countryIcao}
						onFocus={() => changeSelectFocused(true)}
						onBlur={() => changeSelectFocused(false)}
						onChange={(e) => changeCountryIcao(e.target.value)}
					>
						<option value={null} disabled={true}>Select country</option>
						{countries.map(item => (
							<option value={item.icao_code}>{t(`countries.${item.alpha2}`)}</option>
						))}
					</Select>
				</SelectContainer>
				<DocumentsList>
					{documents.length > 0 || countryIcao === null ? documents.map((item) => (
						<DocumentsListItem key={item.type}>
							<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M6.75 8.25L9 10.5L16.5 3" stroke="#6E37D8" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
								<path
									d="M15.75 9V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V3.75C2.25 3.35218 2.40804 2.97064 2.68934 2.68934C2.97064 2.40804 3.35218 2.25 3.75 2.25H12"
									stroke="#6E37D8" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							{item.title}
						</DocumentsListItem>
					)) : (
						<Text
							dangerouslySetInnerHTML={{
								__html: t('accepted.text', {
									country: t(`countries.${country.alpha2}`),
									'accepted.text.tos': `<a target="_blank" rel="nofollow noopener noreferrer" href="${t('accepted.text.tos.url')}">${t('accepted.text.tos')}</a>`,
								}),
							}}
						/>
					)}
				</DocumentsList>
				<ButtonContainer className={buttonsClass}>
					<Button
						name={'mobile_welcome_continue'}
						onClick={() => changeAcceptedDocumentsVisible(false)}
					>
						{t('accepted.docs.close')}
					</Button>
				</ButtonContainer>
			</BottomSliderContent>
		</BottomSliderContainer>
	);
};

const titleClass = css`
  margin: 32px 16px 8px;
`;
const subtitleClass = css`
  margin: 8px 16px 0;
`;
